import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Formik, Form, Field } from 'formik';
import { Box } from 'rebass/styled-components';
import * as Yup from 'yup';

import { FormField, Message, MessageBlock, Button } from '@getro/rombo';

import { put } from 'api';
import strings from 'strings';
import { trackException } from 'helpers/errorTracker';
import pageHoc from 'hocs/pageHoc';
import settings from 'settings';
import { newPasswordSchema } from 'schemas/userPassword';

import SessionLayout from 'components/layout/Session';

const initialValues = { password: '' };

const passwordFormSchema = Yup.object().shape({
  password: newPasswordSchema().required(),
});

const handleSubmit =
  (cancelToken, setSuccess, resetPasswordToken, setExpired) =>
  async (values, { setStatus, setSubmitting }) => {
    setStatus({ error: null });
    const params = {
      password: values.password,
      password_confirmation: values.password,
      reset_password_token: resetPasswordToken,
    };

    try {
      await put('/users/passwords', params, { cancelToken });
      setSuccess(true);
    } catch (error) {
      trackException(error);
      setExpired(true);
    }
    setSubmitting(false);
  };

const pageTitle = 'Set a new password';

const SetNewPassword = () => {
  const history = useHistory();
  const location = useLocation();
  const [success, setSuccess] = useState(false);
  const [expired, setExpired] = useState(false);
  const cancelTokenSource = useRef(axios.CancelToken.source());

  const searchParams = new URLSearchParams(location.search);
  const resetPasswordToken = searchParams.get('reset_password_token');

  useEffect(
    () => () => {
      cancelTokenSource.current.cancel();
    },
    [],
  );

  const handleButtonClick = () => {
    history.push('/login');
  };

  // If no token or expired, show error
  if (!resetPasswordToken || expired) {
    return (
      <SessionLayout title={strings.session.setNewPassword.title}>
        <MessageBlock
          type="warning"
          title="Expired link"
          content="Looks like this link has expired. To reset your password please start the process again."
          buttonText={`Continue to ${settings.companyName}`}
          onClick={handleButtonClick}
        />
      </SessionLayout>
    );
  }

  if (success) {
    return (
      <SessionLayout>
        <MessageBlock
          title="Password updated"
          type="success"
          content="You can now log in using your new password."
          buttonText={`Continue to ${settings.companyName}`}
          onClick={handleButtonClick}
        />
      </SessionLayout>
    );
  }

  return (
    <SessionLayout
      className="SetNewPassword"
      title={strings.session.setNewPassword.title}
      subtitle="Enter your new password, remember that it needs at least 12 characters."
    >
      <Box pt="40px">
        <Formik
          validationSchema={passwordFormSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit(cancelTokenSource.current.token, setSuccess, resetPasswordToken, setExpired)}
        >
          {({ status, isSubmitting }) => (
            <Form>
              <Field type="password" name="password" label="New password" size="lg" component={FormField} />
              <Button size="large" type="submit" width="100%" justifyContent="center" loading={isSubmitting}>
                Set Password
              </Button>
              {status && status.error && (
                <Message className="SetNewPassword__error-message" content={status.error} type="error" />
              )}
            </Form>
          )}
        </Formik>
      </Box>
    </SessionLayout>
  );
};

export default pageHoc({ title: pageTitle })(SetNewPassword);
